/**
 * Session store (U6, zustand): tokens + identity + resolved permissions.
 *
 * Persisted to sessionStorage so a reload keeps the session without leaving
 * long-lived bearer/refresh credentials on disk after the browser closes; the app still
 * re-validates with GET /auth/me on boot (see auth.ts bootstrapSession).
 * Route guards and nav read capability/permission answers from here — always
 * via the shared RBAC matrix, never hard-coded role checks (product law).
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  ROLE_POLICY,
  permissionsForRole,
  type Company,
  type CompanyUser,
  type Permission,
  type RolePolicy,
  type SessionTokens,
} from '@buildledger/shared';

export const SESSION_STORAGE_KEY = 'buildledger.session';
if (typeof window !== 'undefined') {
  // Remove credentials written by versions that used persistent localStorage.
  window.localStorage.removeItem(SESSION_STORAGE_KEY);
}
type SessionResetHandler = () => void;
const sessionResetHandlers = new Set<SessionResetHandler>();

/** Query/cache layers register here so identity changes cannot retain tenant data. */
export function onSessionReset(handler: SessionResetHandler): () => void {
  sessionResetHandlers.add(handler);
  return () => sessionResetHandlers.delete(handler);
}

function notifySessionReset(): void {
  for (const handler of sessionResetHandlers) handler();
}

export interface SessionState {
  tokens: SessionTokens | null;
  user: CompanyUser | null;
  company: Company | null;
  /** Server-resolved permission list (from /auth/me); null until fetched. */
  permissions: readonly Permission[] | null;
  /** True once bootstrapSession() has settled (with or without a session). */
  bootstrapped: boolean;

  setTokens: (tokens: SessionTokens) => void;
  /** Called right after a successful login step (before /auth/me completes). */
  signIn: (tokens: SessionTokens, user: CompanyUser) => void;
  setIdentity: (identity: {
    user: CompanyUser;
    company: Company;
    permissions: readonly Permission[];
  }) => void;
  setBootstrapped: (value: boolean) => void;
  /** Drop the session locally (sign-out, failed refresh, revoked session). */
  clear: () => void;
}

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      tokens: null,
      user: null,
      company: null,
      permissions: null,
      bootstrapped: false,

      setTokens: (tokens) => set({ tokens }),
      signIn: (tokens, user) => {
        notifySessionReset();
        set({ tokens, user, company: null, permissions: null });
      },
      setIdentity: ({ user, company, permissions }) =>
        set({ user, company, permissions, bootstrapped: true }),
      setBootstrapped: (value) => set({ bootstrapped: value }),
      clear: () => {
        notifySessionReset();
        set({ tokens: null, user: null, company: null, permissions: null, bootstrapped: true });
      },
    }),
    {
      name: SESSION_STORAGE_KEY,
      storage: createJSONStorage(() => window.sessionStorage),
      partialize: ({ tokens, user, company, permissions }) => ({
        tokens,
        user,
        company,
        permissions,
      }),
    },
  ),
);

// ---------------------------------------------------------------- selectors

/** Signed in = we hold tokens (identity may still be loading). */
export const selectIsAuthenticated = (s: SessionState): boolean => s.tokens !== null;

/**
 * Permission check backed by the shared matrix. Prefers the server-resolved
 * list from /auth/me; falls back to resolving the user's role locally.
 */
export function sessionCan(s: SessionState, permission: Permission): boolean {
  if (s.permissions) return s.permissions.includes(permission);
  if (s.user) return permissionsForRole(s.user.role).includes(permission);
  return false;
}

/**
 * May this session's role use the web panel at all (ROLE_POLICY surfaces)?
 * Roles without the panel surface get the "use the app" page instead of the
 * shell. Unknown until identity is known — the API refuses such sessions
 * anyway (403 SURFACE_NOT_ALLOWED), so no identity means "don't block".
 */
export function sessionCanUsePanel(s: SessionState): boolean {
  if (!s.user) return true;
  const policy = (ROLE_POLICY as Readonly<Partial<Record<string, RolePolicy>>>)[s.user.role];
  return policy?.surfaces.includes('panel') ?? false;
}

export type Capability = 'contractor' | 'subcontractor';

export function sessionHasCapability(s: SessionState, capability: Capability): boolean {
  if (!s.company) return false;
  return capability === 'contractor' ? s.company.isContractor : s.company.isSubcontractor;
}

/**
 * Candidate landing routes, in sidebar order. Each carries the permission (and,
 * for contractor surfaces, the capability) its route guard in app/router.tsx
 * checks, so the home path is always a page the session can open.
 */
const HOME_CANDIDATES: ReadonlyArray<{
  path: string;
  permission: Permission;
  capability?: Capability;
}> = [
  { path: '/dashboard', permission: 'dashboard:read' },
  { path: '/users', permission: 'users:read' },
  { path: '/projects', permission: 'projects:read', capability: 'contractor' },
  { path: '/schedule', permission: 'schedule:read', capability: 'contractor' },
  { path: '/materials', permission: 'materials:read', capability: 'contractor' },
  { path: '/library', permission: 'library:read' },
];

/** Ungated in the router (every signed-in role can open it). */
const ALWAYS_OPEN_HOME = '/assistant';

/**
 * Landing route for the signed-in session: pure subs live in the sub inbox;
 * everyone else lands on the first page (in nav order) their permissions open,
 * so a session without dashboard:read never loops on a 403.
 */
export function sessionHomePath(s: SessionState): string {
  if (s.company && !s.company.isContractor && s.company.isSubcontractor) return '/sub/inbox';
  // Identity not loaded yet: keep the historical default; guards re-run once it is.
  if (!s.user && !s.permissions) return '/dashboard';
  for (const candidate of HOME_CANDIDATES) {
    if (!sessionCan(s, candidate.permission)) continue;
    // Capability unknown (company not loaded) = skip: RequireCapability would bounce it.
    if (candidate.capability && !sessionHasCapability(s, candidate.capability)) continue;
    return candidate.path;
  }
  return ALWAYS_OPEN_HOME;
}
